import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, getAuthHeaders } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { API_BASE } from '../config/api';
import { resolveAvatarUrl } from '../components/UserAvatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription,
  DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, Users, Plus, MoreVertical, Trash2, Crown,
  Loader2, AtSign, Mail, UserPlus, X as XIcon, Hourglass, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer' };
const ROLE_COLORS = {
  owner: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  admin: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  member: 'bg-green-500/10 text-green-600 border-green-500/20',
  viewer: 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20',
};
const ASSIGNABLE_ROLES = ['admin', 'member', 'viewer'];

const MIN_SEARCH_CHARS = 4;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const WorkspaceMembers = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { workspaces } = useWorkspace();

  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState(null);
  const [myRole, setMyRole] = useState(null);

  // Add member / invite dialog
  const [addOpen, setAddOpen] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [lookupState, setLookupState] = useState('idle'); // idle | searching | found | not_found | error
  const [matchedUser, setMatchedUser] = useState(null);   // { id, name, avatar_url } | null
  const [newRole, setNewRole] = useState('member');
  const [submitting, setSubmitting] = useState(false);
  // Contextual Pro trial offer (Phase 2 Task 5) — set when an invite 403s
  // with trial_available on a Free workspace
  const [trialOffer, setTrialOffer] = useState(null);
  const [startingTrial, setStartingTrial] = useState(false);

  // ----------------- data loading -----------------

  const loadMembers = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_BASE}/workspaces/${workspaceId}/members`,
        { headers: getAuthHeaders() }
      );
      setMembers(res.data || []);
      const me = (res.data || []).find(m => m.user_id === user?.id);
      setMyRole(me?.role || 'viewer');
    } catch (err) {
      toast.error('Failed to load members');
    }
  }, [workspaceId, user]);

  const loadPendingInvites = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_BASE}/workspaces/${workspaceId}/invitations`,
        { headers: getAuthHeaders() }
      );
      setPendingInvites(res.data || []);
    } catch (err) {
      // 403 → not admin; that's expected for non-admins
      if (err?.response?.status !== 403) {
        console.warn('Failed to load pending invites:', err);
      }
      setPendingInvites([]);
    }
  }, [workspaceId]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const ws = workspaces.find(w => w.id === workspaceId);
      if (ws) {
        setWorkspace(ws);
        setMyRole(ws.owner_id === user?.id ? 'owner' : null);
      } else {
        try {
          const wsRes = await axios.get(`${API_BASE}/workspaces/${workspaceId}`, { headers: getAuthHeaders() });
          setWorkspace(wsRes.data);
        } catch (err) {
          // Workspace fetch fallback failed — error will be surfaced by
          // loadMembers below if the workspace genuinely doesn't exist.
          if (import.meta.env.DEV) console.warn('[WorkspaceMembers] workspace fetch failed:', err);
        }
      }
      await Promise.all([loadMembers(), loadPendingInvites()]);
      setLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  // ----------------- email lookup (debounced) -----------------

  const normalizedEmail = useMemo(() => (emailInput || '').trim().toLowerCase(), [emailInput]);
  const isValidEmail = EMAIL_RE.test(normalizedEmail);

  useEffect(() => {
    // Reset state when input changes
    setMatchedUser(null);
    setLookupState('idle');

    if (normalizedEmail.length < MIN_SEARCH_CHARS) return;
    if (!isValidEmail) return;

    const memberEmailMatch = members.find(
      m => (m.user_email || '').toLowerCase() === normalizedEmail
    );
    if (memberEmailMatch) {
      // Already a member — still flag via lookup_email so UI shows clearly
      setLookupState('searching');
    }

    const ctrl = new AbortController();
    setLookupState('searching');
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/users/lookup-email`,
          {
            params: { email: normalizedEmail },
            headers: getAuthHeaders(),
            signal: ctrl.signal,
          }
        );
        if (res.data?.exists && res.data?.user) {
          setMatchedUser(res.data.user);
          setLookupState('found');
        } else {
          setMatchedUser(null);
          setLookupState('not_found');
        }
      } catch (err) {
        if (axios.isCancel?.(err) || err?.code === 'ERR_CANCELED') return;
        setLookupState('error');
        setMatchedUser(null);
      }
    }, 350);

    return () => {
      ctrl.abort();
      clearTimeout(t);
    };
  }, [normalizedEmail, isValidEmail, members]);

  // ----------------- helpers -----------------

  const isAlreadyMember = useMemo(() => {
    if (!matchedUser) return false;
    return members.some(m => m.user_id === matchedUser.id);
  }, [matchedUser, members]);

  const isAlreadyInvited = useMemo(() => {
    if (!normalizedEmail) return false;
    return pendingInvites.some(
      inv => (inv.email || '').toLowerCase() === normalizedEmail
    );
  }, [normalizedEmail, pendingInvites]);

  const resetDialog = () => {
    setEmailInput('');
    setMatchedUser(null);
    setLookupState('idle');
    setNewRole('member');
    setTrialOffer(null);
  };

  // ----------------- actions -----------------

  const detailMessage = (detail) => (typeof detail === 'string' ? detail : detail?.message);

  const handleAddExistingMember = async () => {
    if (!matchedUser) return;
    try {
      setSubmitting(true);
      await axios.post(
        `${API_BASE}/workspaces/${workspaceId}/members`,
        { user_id: matchedUser.id, role: newRole },
        { headers: getAuthHeaders() }
      );
      toast.success(`${matchedUser.name || 'User'} added as ${ROLE_LABELS[newRole]}`);
      setAddOpen(false);
      resetDialog();
      await loadMembers();
    } catch (err) {
      toast.error(detailMessage(err.response?.data?.detail) || 'Failed to add member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendInvite = async () => {
    if (!isValidEmail) return;
    try {
      setSubmitting(true);
      await axios.post(
        `${API_BASE}/workspaces/${workspaceId}/invitations`,
        { email: normalizedEmail, role: newRole },
        { headers: getAuthHeaders() }
      );
      toast.success(`Invite sent to ${normalizedEmail}`);
      setAddOpen(false);
      resetDialog();
      await Promise.all([loadMembers(), loadPendingInvites()]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 503) {
        toast.error(
          'Workspace invitations are not provisioned yet. Run the database migration to enable them.'
        );
      } else if (err.response?.status === 402) {
        toast.error(detailMessage(detail) || 'Seat limit reached. Upgrade your plan to invite more members.');
      } else if (err.response?.status === 403 && detail?.trial_available) {
        // Phase 2 Task 5: the contextual Pro trial is offered ONLY here —
        // the moment a Free owner tries to invite a team role.
        setTrialOffer({
          days: detail.trial_days || 14,
          planName: detail.trial_plan_name || 'Director (Pro)',
        });
      } else {
        toast.error(detailMessage(detail) || 'Failed to send invitation');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartTrial = async () => {
    try {
      setStartingTrial(true);
      const res = await axios.post(
        `${API_BASE}/workspaces/${workspaceId}/trial/start`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(res.data?.message || 'Pro trial started — no card required');
      setTrialOffer(null);
      // Retry the invite that triggered the offer — collaboration is now unlocked
      await handleSendInvite();
    } catch (err) {
      toast.error(detailMessage(err.response?.data?.detail) || 'Failed to start trial');
    } finally {
      setStartingTrial(false);
    }
  };

  const handleChangeRole = async (memberId, role) => {
    try {
      await axios.put(
        `${API_BASE}/workspaces/${workspaceId}/members/${memberId}`,
        { role },
        { headers: getAuthHeaders() }
      );
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, role } : m));
      toast.success('Role updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemove = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from this workspace?`)) return;
    try {
      await axios.delete(
        `${API_BASE}/workspaces/${workspaceId}/members/${memberId}`,
        { headers: getAuthHeaders() }
      );
      setMembers(prev => prev.filter(m => m.id !== memberId));
      toast.success('Member removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleCancelInvite = async (invitationId, email) => {
    if (!window.confirm(`Cancel pending invite to ${email}?`)) return;
    try {
      await axios.delete(
        `${API_BASE}/workspaces/${workspaceId}/invitations/${invitationId}`,
        { headers: getAuthHeaders() }
      );
      setPendingInvites(prev => prev.filter(p => p.id !== invitationId));
      toast.success('Invite cancelled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel invite');
    }
  };

  const isAdmin = myRole === 'owner' || myRole === 'admin';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // ----------------- render -----------------

  return (
    <div className="bg-background">
      <main className="container mx-auto px-4 md:px-6 py-8 max-w-2xl">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/workspaces/${workspaceId}`)} className="-ml-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span className="text-xs font-semibold tracking-wider uppercase">Back</span>
          </Button>
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <h1 className="text-xl md:text-2xl font-bold font-display tracking-tight">
              {workspace?.name ? `${workspace.name} — Members` : 'Members'}
            </h1>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-muted-foreground">
            {members.length} member{members.length !== 1 ? 's' : ''}
            {pendingInvites.length > 0 && (
              <span className="ml-2 text-amber-600">
                · {pendingInvites.length} pending invite{pendingInvites.length !== 1 ? 's' : ''}
              </span>
            )}
          </p>
          {isAdmin && (
            <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-member-btn">
              <Plus className="h-4 w-4 mr-1.5" />
              Add Member
            </Button>
          )}
        </div>

        {/* Active members */}
        <div className="space-y-2">
          {members.map((member) => {
            const isOwner = member.role === 'owner';
            const isSelf = member.user_id === user?.id;
            const canManage = isAdmin && !isOwner && !isSelf;
            return (
              <Card key={member.id} className="p-4 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-sm font-semibold text-primary">
                  {member.user_avatar_url ? (
                    <img
                      src={resolveAvatarUrl(member.user_avatar_url)}
                      alt={member.user_name}
                      className="h-9 w-9 rounded-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    (member.user_name?.[0] || member.user_email?.[0] || '?').toUpperCase()
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-sm truncate">{member.user_name || 'Unknown'}</span>
                    {isOwner && <Crown className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />}
                    {isSelf && <span className="text-[10px] text-muted-foreground">(you)</span>}
                  </div>
                  {/* Only show email to the row itself OR to admins (privacy: hide it from regular members). */}
                  {(isSelf || isAdmin) ? (
                    <p className="text-xs text-muted-foreground truncate">{member.user_email}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground truncate">{ROLE_LABELS[member.role] || member.role}</p>
                  )}
                </div>

                {canManage ? (
                  <Select value={member.role} onValueChange={(v) => handleChangeRole(member.id, v)}>
                    <SelectTrigger className="h-7 w-28 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSIGNABLE_ROLES.map(r => (
                        <SelectItem key={r} value={r} className="text-xs">{ROLE_LABELS[r]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge variant="outline" className={`text-[10px] px-2 border ${ROLE_COLORS[member.role] || ''}`}>
                    {ROLE_LABELS[member.role] || member.role}
                  </Badge>
                )}

                {canManage && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => handleRemove(member.id, member.user_name || member.user_email)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </Card>
            );
          })}
        </div>

        {/* Pending invites (admins only) */}
        {isAdmin && pendingInvites.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xs font-semibold tracking-wider uppercase text-muted-foreground mb-3 flex items-center gap-1.5">
              <Hourglass className="h-3.5 w-3.5" />
              Pending invitations · {pendingInvites.length}
            </h2>
            <div className="space-y-2">
              {pendingInvites.map((inv) => (
                <Card key={inv.id} className="p-4 flex items-center gap-3 border-dashed bg-muted/30" data-testid={`pending-invite-${inv.id}`}>
                  <div className="h-9 w-9 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-4 w-4 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{inv.email}</p>
                    <p className="text-xs text-muted-foreground">
                      Invited {new Date(inv.created_at).toLocaleDateString()}
                      {' · awaiting response'}
                    </p>
                  </div>
                  <Badge variant="outline" className={`text-[10px] px-2 border ${ROLE_COLORS[inv.role] || ''}`}>
                    {ROLE_LABELS[inv.role] || inv.role}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 flex-shrink-0"
                    onClick={() => handleCancelInvite(inv.id, inv.email)}
                    title="Cancel invite"
                  >
                    <XIcon className="h-4 w-4" />
                  </Button>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* ----------------- Add Member / Invite Dialog ----------------- */}
      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) resetDialog(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite to workspace</DialogTitle>
            <DialogDescription>
              Enter the email address of the person you'd like to invite. We'll look up if they
              already have a FrameFlow account — if not, we'll send them an invitation email.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Email input */}
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email address</Label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="name@example.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  className="pl-9"
                  data-testid="member-search-input"
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Search is email-only and never reveals other users' emails. Type at least {MIN_SEARCH_CHARS} characters.
              </p>

              {/* Lookup state */}
              {normalizedEmail.length > 0 && normalizedEmail.length < MIN_SEARCH_CHARS && (
                <p className="text-xs text-muted-foreground">
                  Keep typing… ({normalizedEmail.length}/{MIN_SEARCH_CHARS})
                </p>
              )}

              {normalizedEmail.length >= MIN_SEARCH_CHARS && !isValidEmail && (
                <p className="text-xs text-amber-600">Enter a complete email address.</p>
              )}

              {isValidEmail && lookupState === 'searching' && (
                <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Looking up…
                </div>
              )}

              {/* MATCH — existing FrameFlow user */}
              {isValidEmail && lookupState === 'found' && matchedUser && !isAlreadyMember && !isAlreadyInvited && (
                <div className="flex items-center gap-3 px-3 py-2.5 bg-primary/5 border border-primary/20 rounded-md" data-testid="lookup-found">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary overflow-hidden flex-shrink-0">
                    {matchedUser.avatar_url ? (
                      <img
                        src={resolveAvatarUrl(matchedUser.avatar_url)}
                        alt={matchedUser.name}
                        className="h-8 w-8 object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      (matchedUser.name?.[0] || '?').toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{matchedUser.name || 'FrameFlow user'}</p>
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      Already on FrameFlow — they'll be added instantly.
                    </p>
                  </div>
                </div>
              )}

              {/* MATCH but already in workspace */}
              {isValidEmail && isAlreadyMember && (
                <div className="text-xs text-muted-foreground px-3 py-2 bg-muted/40 rounded-md">
                  This person is already a member of this workspace.
                </div>
              )}

              {/* MATCH but already invited */}
              {isValidEmail && !isAlreadyMember && isAlreadyInvited && (
                <div className="text-xs text-muted-foreground px-3 py-2 bg-amber-500/5 border border-amber-500/20 rounded-md flex items-center gap-2">
                  <Hourglass className="h-3.5 w-3.5 text-amber-600" />
                  This email already has a pending invite.
                </div>
              )}

              {/* NO MATCH — offer to send invite */}
              {isValidEmail && lookupState === 'not_found' && !isAlreadyInvited && (
                <div className="px-3 py-2.5 bg-amber-500/5 border border-amber-500/20 rounded-md" data-testid="lookup-not-found">
                  <p className="text-sm flex items-center gap-2 mb-1">
                    <UserPlus className="h-4 w-4 text-amber-600" />
                    <span>No FrameFlow account found</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    We'll send an email invitation. They'll join your workspace after signing up
                    with this email address.
                  </p>
                </div>
              )}

              {isValidEmail && lookupState === 'error' && (
                <p className="text-xs text-destructive">Lookup failed. Please try again.</p>
              )}
            </div>

            {/* Role selector */}
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger data-testid="role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSIGNABLE_ROLES.map(r => (
                    <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {newRole === 'admin' && 'Can manage members and films.'}
                {newRole === 'member' && 'Can create and edit films. Uses a paid seat.'}
                {newRole === 'viewer' && 'Read-only access. Free — does not consume a seat.'}
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            {/* Contextual 14-day Pro trial offer (Phase 2 Task 5) */}
            {trialOffer && (
              <div
                className="w-full px-3 py-3 bg-primary/5 border border-primary/25 rounded-md"
                data-testid="trial-offer-box"
              >
                <p className="text-sm font-medium flex items-center gap-2 mb-1">
                  <Sparkles className="h-4 w-4 text-primary" />
                  Team roles need a paid plan
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  Try {trialOffer.planName} free for {trialOffer.days} days — no card required.
                  Your invite will be sent as soon as the trial starts. You can also invite this
                  person as a viewer (free) instead.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    onClick={handleStartTrial}
                    disabled={startingTrial || submitting}
                    data-testid="start-trial-btn"
                  >
                    {startingTrial ? (
                      <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Starting…</>
                    ) : (
                      <><Sparkles className="h-4 w-4 mr-1.5" />Start free trial</>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate('/billing')}
                    data-testid="trial-offer-upgrade-btn"
                  >
                    Upgrade instead
                  </Button>
                </div>
              </div>
            )}

            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={submitting}>
              Cancel
            </Button>

            {/* Add existing user button */}
            {isValidEmail && lookupState === 'found' && matchedUser && !isAlreadyMember && !isAlreadyInvited && (
              <Button
                onClick={handleAddExistingMember}
                disabled={submitting}
                data-testid="confirm-add-member"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Adding…</>
                ) : (
                  <>Add as {ROLE_LABELS[newRole]}</>
                )}
              </Button>
            )}

            {/* Send invite button */}
            {isValidEmail && lookupState === 'not_found' && !isAlreadyInvited && (
              <Button
                onClick={handleSendInvite}
                disabled={submitting}
                data-testid="confirm-send-invite"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Sending…</>
                ) : (
                  <><Mail className="h-4 w-4 mr-1.5" />Send invite</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkspaceMembers;
